import { TablePlugin } from '@platejs/table/react';

// Excel-style single-cell selection.
//
// @platejs/table's native cell selection has a hardcoded ">1 cell" gate, so it
// can never represent a SINGLE selected cell. We hold the selected cell NODE in
// a TablePlugin option so that ONE source of truth drives both:
//   - the cell renderer's highlight (reactive, via usePluginOption), and
//   - the editor-shell keydown handler (imperative get/setOption — arrows,
//     type-to-overwrite, Escape).
// The option is editor-wide on purpose: only one cell can be single-selected at
// a time, even across two tables in the same document.
//
// The option MUST be declared on the plugin (see usePlateEditorLogic:
// `.configure({ options: { [CELL_SELECTION_OPTION]: null } })`) — otherwise
// usePluginOption has no atom to subscribe to and the highlight won't react to
// setOption.
//
// "Selected" = the cell's ENTIRE content is range-selected, with the caret and
// native ::selection hidden by CSS (.cellSelected). A full-content range (not a
// collapsed caret) is deliberate: it makes type-to-overwrite, Cmd+C, and
// toolbar formatting target the whole cell for free — the Excel feel — while CSS
// turns the range into a clean cell highlight.

export const CELL_SELECTION_OPTION = 'singleSelectedCell';

// Node types that count as a table cell across the plugin's vocabulary
// (rendered td/th + the base plugin keys). Shared so the cell-editing cursor
// check and the keyboard handler agree on what a "cell" is.
export const CELL_TYPES = ['td', 'th', 'table_cell', 'table_cell_header'];

export function getSelectedCell(editor: any): any | null {
    try {
        return (editor as any)?.getOption?.(TablePlugin, CELL_SELECTION_OPTION) ?? null;
    } catch {
        return null;
    }
}

// Drop the single-cell selection. `collapse` moves the caret to the cell start
// so no stray full-cell range lingers (used by Escape); the default leaves the
// selection in place (used before a type-to-overwrite, so the char replaces it).
export function clearCellSelection(editor: any, collapse = false): void {
    try {
        const cell = getSelectedCell(editor);
        (editor as any)?.setOption?.(TablePlugin, CELL_SELECTION_OPTION, null);
        if (collapse && cell) {
            const path = editor?.api?.findPath?.(cell);
            const start = path && editor?.api?.start?.(path);
            if (start) editor?.tf?.select?.(start);
        }
    } catch {
        /* defensive: editor / api may not be ready */
    }
}

// Range-select the whole cell, mark it the single-selected cell, and focus.
// Shared by left-click, right-click, and arrow navigation so they all land in
// the identical "selected" state.
export function selectCell(editor: any, cellNode: any): void {
    try {
        const path = editor?.api?.findPath?.(cellNode);
        if (!path) return;
        const start = editor?.api?.start?.(path);
        const end = editor?.api?.end?.(path);
        if (!start || !end) return;
        editor?.tf?.select?.({ anchor: start, focus: end });
        (editor as any)?.setOption?.(TablePlugin, CELL_SELECTION_OPTION, cellNode);
        editor?.tf?.focus?.();
    } catch {
        /* defensive */
    }
}

// Distinguish a CLICK from a DRAG on a left mousedown over a table cell.
//
// @platejs/table builds its multi-cell range purely from editor.selection: a
// native contentEditable drag extends editor.selection across cells and the
// library reads that off via getTableGridAbove (>1 gate). So for a DRAG we must
// NOT preventDefault and NOT single-select — the native range owns the
// highlight. For a CLICK (no/below-threshold movement) we run selectCell to
// restore the Excel single-cell select.
//
// mousedown fires before any pointer movement, so the decision is deferred to
// mouseup. This arms transient window listeners and, on mouseup, calls back
// with whether the gesture was a drag. Returns a teardown for callers that
// unmount mid-gesture.
const DRAG_THRESHOLD_PX = 4;

export function watchCellDrag(
    startX: number,
    startY: number,
    onResolve: (dragged: boolean) => void,
    threshold: number = DRAG_THRESHOLD_PX,
): () => void {
    let dragged = false;
    const onMove = (e: MouseEvent) => {
        if (Math.abs(e.clientX - startX) > threshold || Math.abs(e.clientY - startY) > threshold) {
            dragged = true;
        }
    };
    const teardown = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
    };
    const onUp = () => {
        teardown();
        onResolve(dragged);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return teardown;
}

// Move the selection to the adjacent cell, clamped at the table edges. Returns
// true if the key was a navigation key we own (so the caller preventDefaults),
// including the no-op at-an-edge case. Slate cell path = [...tablePath, row, col].
export function moveCellSelection(editor: any, cellNode: any, key: string): boolean {
    try {
        const cellPath: number[] | undefined = editor?.api?.findPath?.(cellNode);
        if (!cellPath || cellPath.length < 2) return false;
        const colIdx = cellPath[cellPath.length - 1];
        const rowIdx = cellPath[cellPath.length - 2];
        const tablePath = cellPath.slice(0, -2);
        const tableNode: any = editor?.api?.node?.(tablePath)?.[0];
        const rows: any[] = tableNode?.children ?? [];
        if (!rows.length) return false;

        let r = rowIdx;
        let c = colIdx;
        if (key === 'ArrowUp') r = Math.max(0, rowIdx - 1);
        else if (key === 'ArrowDown') r = Math.min(rows.length - 1, rowIdx + 1);
        else if (key === 'ArrowLeft') c = Math.max(0, colIdx - 1);
        else if (key === 'ArrowRight') c = Math.min((rows[rowIdx]?.children?.length ?? 1) - 1, colIdx + 1);
        else return false;

        const targetRow = rows[r];
        const targetWidth = targetRow?.children?.length ?? 0;
        if (!targetWidth) return false;
        c = Math.min(c, targetWidth - 1); // clamp the column to the target row's width

        if (r === rowIdx && c === colIdx) return true; // already at the edge — swallow, don't move
        selectCell(editor, targetRow.children[c]);
        return true;
    } catch {
        return false;
    }
}
